import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  PROGRESS_DIALOG_CANCEL_RESULT,
  ProgressDialogComponent,
} from '@daas-consulting/core-frontend-components';
import * as utils from '@daas-consulting/core-frontend-utils';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { Subscription, TimeoutError, take, timeout } from 'rxjs';
import * as congratsActions from '../congrats/congrats.actions';
import * as loaderActions from '../loader/loader.actions';
import * as actions from './download.actions';
import {
  DEFAULT_HINT,
  DEFAULT_MESSAGE,
  DEFAULT_TITLE,
  FAILURE_MESSAGE,
  TIMEOUT_MESSAGE,
  name,
} from './download.consts';
import { DownloadStateModel } from './download.state-model';

const defaults: DownloadStateModel = {
  isDownloading: false,
};

/**
 * DownloadState
 *
 * Dueño único de la descarga de archivos en curso. Concentra las cuatro cosas
 * que antes no existían en ninguna parte y que cada pantalla resolvía (mal) por
 * su cuenta:
 *
 * - **Señal de que el sistema trabaja**: despacha `ShowLoading`/`HideLoading`,
 *   que encienden la `mat-progress-bar` del layout, y abre un diálogo
 *   bloqueante con spinner.
 * - **Bloqueo de reentrada**: `isDownloading` deshabilita el botón que dispara
 *   la descarga y además funciona como cerrojo acá adentro.
 * - **Referencia a la descarga pendiente**: se guarda la `Subscription`, así
 *   que se puede cancelar. Desuscribir un `HttpClient` con `withFetch()` aborta
 *   el `fetch`, que cierra el socket — de ahí se entera el backend.
 * - **Techo de espera**: `DOWNLOAD_TIMEOUT_MS`.
 *
 * Sigue el mismo patrón que `congrats.state.ts`, que también es un state cuyo
 * trabajo real es manejar una pieza de Material (ahí `MatSnackBar`, acá
 * `MatDialog`).
 * @author daas software s.r.l <hola@daas.consulting>
 */
@State<DownloadStateModel>({
  name,
  defaults,
})
@Injectable()
export class DownloadState {
  readonly #dialog: MatDialog;
  readonly #store: Store;
  #subscription: Subscription | null = null;
  #dialogRef: MatDialogRef<ProgressDialogComponent, string> | null = null;

  constructor(dialog: MatDialog, store: Store) {
    this.#dialog = dialog;
    this.#store = store;
  }

  @Action(actions.StartDownload)
  startDownload(
    ctx: StateContext<DownloadStateModel>,
    { payload }: actions.StartDownload
  ): void {
    // Cerrojo: el botón ya viene deshabilitado, pero un segundo disparo por
    // teclado o por una acción encolada no puede pisar la descarga en curso.
    if (ctx.getState().isDownloading) {
      return;
    }

    ctx.patchState({ isDownloading: true });
    this.#store.dispatch(new loaderActions.ShowLoading());
    this._openDialog(payload);

    this.#subscription = payload.blob$
      .pipe(
        // La cadena de interceptors emite la respuesta pero nunca completa, así
        // que sin take(1) el timeout seguiría armado después de la descarga.
        take(1),
        timeout({ first: utils.download.DOWNLOAD_TIMEOUT_MS })
      )
      .subscribe({
        next: (blob) => {
          utils.download.downloadBlobAsFile(blob, payload.fileName);
          this.#store.dispatch(new actions.DownloadSuccessful());
        },
        error: (error) => {
          this.#store.dispatch(new actions.DownloadFailure({ error }));
        },
      });
  }

  @Action(actions.DownloadSuccessful)
  downloadSuccessful(ctx: StateContext<DownloadStateModel>): void {
    this._finish(ctx);
  }

  @Action(actions.DownloadFailure)
  downloadFailure(
    ctx: StateContext<DownloadStateModel>,
    { payload }: actions.DownloadFailure
  ): void {
    this._finish(ctx);
    const message =
      payload.error instanceof TimeoutError ? TIMEOUT_MESSAGE : FAILURE_MESSAGE;
    this.#store.dispatch(new congratsActions.ShowErrorMessage(message));
  }

  @Action(actions.CancelDownload)
  cancelDownload(ctx: StateContext<DownloadStateModel>): void {
    if (!ctx.getState().isDownloading) {
      return;
    }

    this._finish(ctx);
  }

  private _openDialog(payload: actions.StartDownloadPayload): void {
    this.#dialogRef = this.#dialog.open<
      ProgressDialogComponent,
      unknown,
      string
    >(ProgressDialogComponent, {
      // Sin backdrop ni ESC: mientras se genera el archivo la única salida es
      // Cancelar, y así no queda una descarga corriendo sin nada que la
      // represente en pantalla.
      disableClose: true,
      data: {
        title: payload.title ?? DEFAULT_TITLE,
        message: payload.message ?? DEFAULT_MESSAGE,
        hint: payload.hint ?? DEFAULT_HINT,
      },
    });

    this.#dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result === PROGRESS_DIALOG_CANCEL_RESULT) {
          this.#store.dispatch(new actions.CancelDownload());
        }
      });
  }

  /**
   * _finish
   *
   * Camino único de salida — éxito, error, timeout y cancelación pasan por
   * acá. El contador del loader está clampeado en cero, pero un `ShowLoading`
   * sin su `HideLoading` deja la barra encendida para siempre, así que no puede
   * haber más de un lugar que la apague.
   *
   * Desuscribir es lo que aborta el `fetch` en curso: es la cancelación real,
   * no sólo el cierre del diálogo.
   */
  private _finish(ctx: StateContext<DownloadStateModel>): void {
    this.#subscription?.unsubscribe();
    this.#subscription = null;

    const dialogRef = this.#dialogRef;
    this.#dialogRef = null;
    dialogRef?.close();

    ctx.patchState({ isDownloading: false });
    this.#store.dispatch(new loaderActions.HideLoading());
  }
}
